// src/App.js
import React, { useEffect, useMemo, useState } from "react";

/* =========================
   Local storage keys
   ========================= */
const LS_WORKER_URL = "FDM_WORKER_URL"; // Cloudflare Worker base URL
const LS_PUBLIC_TOKEN = "FDM_PUBLIC_DEMO_TOKEN"; // Worker bearer token
const STORE_KEY = "fdm_content_studio_v1";

/* =========================
   Asset presets → engine hints
   (We call the underlying engine “Fairway” in the UI)
   ========================= */
const ASSET_OPTIONS = [
  "PPT",
  "eBook",
  "Case Study",
  "Infographic",
  "Ad Creative",
  "Research Report",
  "One-Pager",
  "Proposal",
];

const ASSET_PRESETS = {
  PPT: {
    format: "presentation",
    numCards: 10,
    themeName: "Oasis",
    cardSplit: "auto",
  },
  eBook: {
    format: "document",
    numCards: 12,
    themeName: "Oasis",
    cardSplit: "auto",
  },
  "Case Study": {
    format: "document",
    numCards: 8,
    themeName: "Oasis",
    cardSplit: "auto",
  },
  Infographic: {
    format: "presentation",
    numCards: 6,
    themeName: "Oasis",
    cardSplit: "auto",
  },
  "Ad Creative": {
    format: "presentation",
    numCards: 4,
    themeName: "Oasis",
    cardSplit: "auto",
  },
  "Research Report": {
    format: "document",
    numCards: 14,
    themeName: "Oasis",
    cardSplit: "auto",
  },
  "One-Pager": {
    format: "document",
    numCards: 1,
    themeName: "Oasis",
    cardSplit: "auto",
  },
  Proposal: {
    format: "document",
    numCards: 6,
    themeName: "Oasis",
    cardSplit: "auto",
  },
};

/* =========================
   Small helpers
   ========================= */
const setLocal = (k, v) => localStorage.setItem(k, v ?? "");
const getLocal = (k) => localStorage.getItem(k) ?? "";

function askOnce(promptText, key) {
  const v = window.prompt(promptText, getLocal(key) || "") || "";
  setLocal(key, v);
  return v;
}

function requireWorkerAuth() {
  let base = getLocal(LS_WORKER_URL);
  if (!base)
    base = askOnce(
      "Worker URL (e.g., https://gamma-openai-proxy.benton-161.workers.dev):",
      LS_WORKER_URL
    );
  let token = getLocal(LS_PUBLIC_TOKEN);
  if (!token) token = askOnce("Worker PUBLIC_DEMO_TOKEN:", LS_PUBLIC_TOKEN);
  return { base: base.replace(/\/+$/, ""), token: token.trim() };
}

/* =========================
   Proxy-only client (Worker)
   ========================= */
async function postJSON(path, body) {
  const { base, token } = requireWorkerAuth();
  const r = await fetch(`${base}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const text = await r.text();
  if (!r.ok) throw new Error(text || `${path} failed: ${r.status}`);
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function getJSON(path) {
  const { base, token } = requireWorkerAuth();
  const r = await fetch(`${base}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const text = await r.text();
  if (!r.ok) throw new Error(text || `${path} failed: ${r.status}`);
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/* ---- Fairway engine (Gamma under the hood) via Worker ---- */
async function engineCreate(payload) {
  // Worker route implemented in your Worker: POST /api/gamma/generations
  return postJSON("/api/gamma/generations", payload);
}

async function engineStatus(id) {
  // Worker route implemented in your Worker: GET /api/gamma/generations/:id
  return getJSON(`/api/gamma/generations/${encodeURIComponent(id)}`);
}

/* ---- Worker AI-brief route ---- */
async function fillBriefWithAI({ url, text, companyHint }) {
  // Worker route implemented in your Worker: POST /ai-brief
  return postJSON("/ai-brief", { url, text, companyHint });
}

/* =========================
   Brief → Engine text
   ========================= */
function computeEngineText({
  title,
  assetType,
  company,
  theme,
  aiContext,
  insights,
  whyMatters,
  howWeHelp,
  proofs,
  notes,
  evidenceUrl,
}) {
  const lines = [];
  lines.push(`# Title: ${title || "Content draft"}`);
  if (assetType) lines.push(`_Asset Type_: ${assetType}`);
  if (evidenceUrl) {
    lines.push(`## Evidence / inspiration link`);
    lines.push(evidenceUrl);
  }
  if (company || theme) {
    lines.push(`## Context`);
    lines.push(
      `Company: ${company || "—"}${company && theme ? " · " : ""}${
        theme ? `Theme: ${theme}` : ""
      }`
    );
  }
  if (aiContext) {
    lines.push(`## AI context (critical)`);
    lines.push(aiContext);
  }
  if (insights.length) {
    lines.push(`## Key Insights`);
    insights.slice(0, 2).forEach((i) => lines.push(`- ${i}`));
  }
  if (whyMatters) {
    lines.push(`## Why It Matters`);
    lines.push(`- ${whyMatters}`);
  }
  if (howWeHelp) {
    lines.push(`## How We Help`);
    lines.push(`${howWeHelp}`);
  }
  if (proofs.length) {
    lines.push(`## Credibility`);
    proofs.slice(0, 3).forEach((p) => lines.push(`- ${p}`));
  }
  if (notes) {
    lines.push(`## Notes`);
    lines.push(notes);
  }

  let out = lines.join("\n");
  const MAX = 400_000;
  if (out.length > MAX)
    out = out.slice(0, MAX - 1200) + `\n\n[Truncated to 400,000 char limit]`;
  return out;
}

/* =========================
   CSV helpers
   ========================= */
const CSV_HEADERS = [
  "evidenceUrl",
  "title",
  "assetType",
  "company",
  "theme",
  "aiContext",
  "insights",
  "whyMatters",
  "howWeHelp",
  "proofs",
  "notes",
];

function toCsvRow(v) {
  return `"${String(v ?? "").replace(/"/g, '""')}"`;
}

function downloadTextFile(name, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter(Boolean);
  if (!lines.length) return [];
  const header = lines[0].split(",").map((h) => h.replace(/^"|"$/g, ""));
  const rows = lines.slice(1).map((line) => {
    const cols = [];
    let cur = "";
    let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQ) {
        if (ch === '"' && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else if (ch === '"') {
          inQ = false;
        } else {
          cur += ch;
        }
      } else {
        if (ch === '"') inQ = true;
        else if (ch === ",") {
          cols.push(cur);
          cur = "";
        } else cur += ch;
      }
    }
    cols.push(cur);
    const obj = {};
    header.forEach((h, i) => (obj[h] = cols[i] ?? ""));
    return obj;
  });
  return rows;
}

/* =========================
   App
   ========================= */
export default function App() {
  // Link & AI context
  const [evidenceUrl, setEvidenceUrl] = useState("");
  const [aiContext, setAiContext] = useState(
    "Example: We want a concise, sales-ready deck that explains how verified data and human callers improve SDR meeting quality. Audience: VP Demand Gen. Avoid buzzwords; show metrics and simple diagrams."
  );

  // Brief fields
  const [title, setTitle] = useState("Content draft");
  const [assetType, setAssetType] = useState("PPT");
  const [company, setCompany] = useState("");
  const [theme, setTheme] = useState("");
  const [insights, setInsights] = useState([]);
  const [whyMatters, setWhyMatters] = useState("");
  const [howWeHelp, setHowWeHelp] = useState("");
  const [proofs, setProofs] = useState([]);
  const [notes, setNotes] = useState("");

  // UI state
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [routeLabel] = useState("Proxy"); // always proxy now
  const [aiBusy, setAiBusy] = useState(false);

  // Persist/restore
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORE_KEY);
      if (!raw) return;
      const s = JSON.parse(raw);
      [
        ["evidenceUrl", setEvidenceUrl],
        ["aiContext", setAiContext],
        ["title", setTitle],
        ["assetType", setAssetType],
        ["company", setCompany],
        ["theme", setTheme],
        ["insights", (v) => setInsights(v || [])],
        ["whyMatters", setWhyMatters],
        ["howWeHelp", setHowWeHelp],
        ["proofs", (v) => setProofs(v || [])],
        ["notes", setNotes],
      ].forEach(([k, f]) => k in s && f(s[k]));
    } catch {}
  }, []);

  useEffect(() => {
    localStorage.setItem(
      STORE_KEY,
      JSON.stringify({
        evidenceUrl,
        aiContext,
        title,
        assetType,
        company,
        theme,
        insights,
        whyMatters,
        howWeHelp,
        proofs,
        notes,
      })
    );
  }, [
    evidenceUrl,
    aiContext,
    title,
    assetType,
    company,
    theme,
    insights,
    whyMatters,
    howWeHelp,
    proofs,
    notes,
  ]);

  // Build engine payload
  const engineInputText = useMemo(
    () =>
      computeEngineText({
        title,
        assetType,
        company,
        theme,
        aiContext,
        insights,
        whyMatters,
        howWeHelp,
        proofs,
        notes,
        evidenceUrl,
      }),
    [
      title,
      assetType,
      company,
      theme,
      aiContext,
      insights,
      whyMatters,
      howWeHelp,
      proofs,
      notes,
      evidenceUrl,
    ]
  );

  const inputCharCount = engineInputText.length;
  const overLimit = inputCharCount > 400000;

  function buildFairwayPayload() {
    const preset = ASSET_PRESETS[assetType] || ASSET_PRESETS["PPT"];
    return {
      inputText: engineInputText,
      textMode: "generate",
      // Use preset mapping
      format: preset.format,
      themeName: preset.themeName,
      numCards: preset.numCards,
      cardSplit: preset.cardSplit,
      // Light guidance so the engine shapes for the asset
      additionalInstructions: `Produce a polished ${assetType} suitable for enterprise buyers; keep brand-neutral styling.`,
      textOptions: { amount: "detailed", tone: "professional", language: "en" },
      imageOptions: { source: "noImages" },
      cardOptions: { dimensions: "fluid" },
      sharingOptions: { workspaceAccess: "view", externalAccess: "noAccess" },
    };
  }

  // Create Fairway draft (Gamma via Worker)
  async function createFairwayDraft() {
    setErr("");
    if (overLimit) {
      setErr(
        `Your content is ${inputCharCount.toLocaleString()} characters. Max is 400,000. Please trim and try again.`
      );
      return;
    }
    try {
      setBusy(true);
      setNote("Creating draft…");

      // sanity: ensure Worker URL + token are set
      const { base, token } = requireWorkerAuth();
      if (!base || !token) throw new Error("Worker URL or token missing.");

      const payload = buildFairwayPayload();
      const created = await engineCreate(payload);
      const genId = created?.id || created?.generationId || created?.data?.id;
      if (!genId)
        throw new Error(
          "Draft engine did not return an id: " + JSON.stringify(created)
        );

      const start = Date.now();
      const timeoutMs = 120000;
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

      let last;
      while (Date.now() - start < timeoutMs) {
        setNote("Creating draft… (checking status)");
        last = await engineStatus(genId);
        if (last.status === "completed") break;
        if (["failed", "canceled"].includes(last.status)) {
          throw new Error(`Generation ${last.status}`);
        }
        await sleep(2000);
      }
      if (last?.status !== "completed")
        throw new Error("Timed out waiting for draft completion.");

      setResult({
        status: last.status,
        viewUrl: last?.result?.url || last?.result?.viewUrl || null,
        exportUrl: last?.result?.exportUrl || null,
      });
      setNote("Done!");
    } catch (e) {
      const msg = String(e?.message || e);
      const corsHint = /Failed to fetch/i.test(msg)
        ? " (likely CORS). Ensure your Worker URL & ALLOWED_ORIGINS are set, then try again."
        : "";
      setErr(`Error: ${msg}${corsHint}`);
      setNote("");
    } finally {
      setBusy(false);
    }
  }

  // AI: fill brief using Worker
  async function handleFillWithAI() {
    try {
      setAiBusy(true);
      const data = await fillBriefWithAI({
        url: (evidenceUrl || "").trim(),
        text: aiContext || "",
        companyHint: company || "",
      });
      if (data.company) setCompany(data.company);
      if (data.theme) setTheme(data.theme);
      if (Array.isArray(data.insightBigIdea))
        setInsights(data.insightBigIdea.slice(0, 2));
      if (data.stakeIfNoChange) setWhyMatters(data.stakeIfNoChange);
      if (data.whyUsSummary) setHowWeHelp(data.whyUsSummary);
      if (Array.isArray(data.signalsList))
        setProofs(data.signalsList.slice(0, 3));
    } catch (e) {
      alert(e.message || String(e));
    } finally {
      setAiBusy(false);
    }
  }

  /* ---------- CSV actions ---------- */
  function downloadSampleCsv() {
    const sample = {
      evidenceUrl: "https://example.com/source-article",
      title: "Content draft",
      assetType: "PPT",
      company: "Acme Inc.",
      theme: "Q4 pipeline creation",
      aiContext:
        "We want a concise, sales-ready deck for VP Demand Gen. Avoid buzzwords; show clear metrics, simple diagrams, and next steps.",
      insights: "Intent is trending; Existing contacts under-utilized",
      whyMatters: "Missing the window means higher CAC and lost share.",
      howWeHelp:
        "Verified data + expert human callers → more qualified meetings, faster.",
      proofs: "Case study: +41% meeting rate; SOC 2; 200+ logos",
      notes: "Use brand colors only; no gradients.",
    };
    const header = CSV_HEADERS.map(toCsvRow).join(",");
    const row = CSV_HEADERS.map((k) =>
      toCsvRow(k === "insights" || k === "proofs" ? sample[k] : sample[k] || "")
    ).join(",");
    downloadTextFile("sample-brief.csv", `${header}\n${row}\n`);
  }

  function exportCurrentCsv() {
    const flat = {
      evidenceUrl,
      title,
      assetType,
      company,
      theme,
      aiContext,
      insights: insights.join(" | "),
      whyMatters,
      howWeHelp,
      proofs: proofs.join(" | "),
      notes,
    };
    const header = CSV_HEADERS.map(toCsvRow).join(",");
    const row = CSV_HEADERS.map((k) => toCsvRow(flat[k] || "")).join(",");
    downloadTextFile("brief.csv", `${header}\n${row}\n`);
  }

  async function uploadCsv(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const rows = parseCsv(text);
    if (!rows.length) return alert("No rows found.");
    const r = rows[0]; // first row only for this UI
    setEvidenceUrl(r.evidenceUrl || "");
    setTitle(r.title || "Content draft");
    setAssetType(r.assetType || "PPT");
    setCompany(r.company || "");
    setTheme(r.theme || "");
    setAiContext(r.aiContext || "");
    setInsights(
      (r.insights || "")
        .split(/\s*\|\s*/)
        .filter(Boolean)
        .slice(0, 2)
    );
    setWhyMatters(r.whyMatters || "");
    setHowWeHelp(r.howWeHelp || "");
    setProofs(
      (r.proofs || "")
        .split(/\s*\|\s*/)
        .filter(Boolean)
        .slice(0, 3)
    );
    setNotes(r.notes || "");
    e.target.value = "";
  }

  /* ---------- UI helpers ---------- */
  function resetWorkerUrl() {
    setLocal(LS_WORKER_URL, "");
    alert("Worker URL cleared. Use “Set Worker URL & token” to re-enter.");
  }
  function resetWorkerToken() {
    setLocal(LS_PUBLIC_TOKEN, "");
    alert("Worker token cleared. Use “Set Worker URL & token” to re-enter.");
  }
  function setWorkerAndToken() {
    askOnce("Worker URL (https://…workers.dev):", LS_WORKER_URL);
    askOnce("Worker PUBLIC_DEMO_TOKEN:", LS_PUBLIC_TOKEN);
    alert("Saved.");
  }

  return (
    <div style={styles.wrap}>
      <header style={styles.header}>
        <div style={styles.badge}>F</div>
        <div>
          <div style={styles.brand}>Fairway Content Studio</div>
          <div style={styles.sub}>Brief → draft, fast.</div>
        </div>
      </header>

      {/* CSV Bar */}
      <section style={styles.card}>
        <div style={styles.cardTitle}>CSV Brief</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={styles.chipBtn} onClick={downloadSampleCsv}>
            Download sample CSV
          </button>
          <button style={styles.chipBtn} onClick={exportCurrentCsv}>
            Export current as CSV
          </button>
          <label style={{ ...styles.chipBtn, cursor: "pointer" }}>
            Upload CSV brief
            <input
              type="file"
              accept=".csv"
              style={{ display: "none" }}
              onChange={uploadCsv}
            />
          </label>
        </div>
        <small style={{ color: "#6b7280" }}>
          Upload a CSV to prefill briefs in bulk, then send to Fairway.
        </small>
      </section>

      {/* Link & AI context */}
      <section style={{ ...styles.card, marginTop: 12 }}>
        <div style={styles.cardTitle}>Link</div>
        <label style={styles.label}>
          Evidence / inspiration URL (optional)
        </label>
        <input
          style={styles.input}
          value={evidenceUrl}
          onChange={(e) => setEvidenceUrl(e.target.value)}
          placeholder="https://example.com/source-article"
        />

        <label style={styles.label}>AI context (critical for brief)</label>
        <textarea
          rows={3}
          style={{ ...styles.input, resize: "vertical" }}
          value={aiContext}
          onChange={(e) => setAiContext(e.target.value)}
          placeholder="Provide concise context the AI must know (goal, audience, constraints, examples)."
        />

        <div style={{ ...styles.actions, marginTop: 8 }}>
          <button
            type="button"
            style={styles.primary}
            onClick={handleFillWithAI}
            disabled={aiBusy}
          >
            {aiBusy ? "Filling…" : "Fill with AI"}
          </button>
        </div>
      </section>

      {/* Brief fields */}
      <section style={{ ...styles.card, marginTop: 12 }}>
        <div style={styles.cardTitle}>Brief</div>

        <Grid two>
          <Small label="Title" value={title} onChange={setTitle} />
          <div>
            <label style={styles.label}>Asset Type</label>
            <select
              style={{ ...styles.input, height: 42 }}
              value={assetType}
              onChange={(e) => setAssetType(e.target.value)}
            >
              {ASSET_OPTIONS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
            <small style={{ color: "#6b7280" }}>
              We’ll send asset-specific guidance and pick the right Fairway
              format.
            </small>
          </div>
        </Grid>

        <Grid two>
          <Small label="Company" value={company} onChange={setCompany} />
          <Small label="Theme" value={theme} onChange={setTheme} />
        </Grid>

        <label style={styles.label}>Key Insights (1–2 lines)</label>
        <textarea
          rows={2}
          style={{ ...styles.input, resize: "vertical" }}
          value={insights.join("\n")}
          onChange={(e) =>
            setInsights(
              e.target.value
                .split(/\n/)
                .map((s) => s.trim())
                .filter(Boolean)
                .slice(0, 2)
            )
          }
          placeholder="One idea per line…"
        />

        <Grid two>
          <Small
            label="Why It Matters (1 line)"
            value={whyMatters}
            onChange={setWhyMatters}
          />
          <Small
            label="How We Help"
            value={howWeHelp}
            onChange={setHowWeHelp}
          />
        </Grid>

        <label style={styles.label}>Credibility (up to 3)</label>
        <textarea
          rows={2}
          style={{ ...styles.input, resize: "vertical" }}
          value={proofs.join("\n")}
          onChange={(e) =>
            setProofs(
              e.target.value
                .split(/\n/)
                .map((s) => s.trim())
                .filter(Boolean)
                .slice(0, 3)
            )
          }
          placeholder="One per line… (e.g., Case study: +41% meeting rate)"
        />

        <label style={styles.label}>Notes</label>
        <textarea
          rows={3}
          style={{ ...styles.input, resize: "vertical" }}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional production notes, constraints, or must-include items…"
        />

        <div style={styles.actions}>
          <button
            type="button"
            style={styles.primary}
            onClick={createFairwayDraft}
            disabled={busy}
          >
            {busy ? "Creating…" : "Create Fairway Draft"}
          </button>
        </div>

        <div style={{ marginTop: 8, fontSize: 13 }}>
          {note ? <span>{note}</span> : null}
          {err ? (
            <div style={{ color: "#b91c1c", marginTop: 6 }}>{err}</div>
          ) : null}
          <div style={{ marginTop: 6 }}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                resetWorkerToken();
              }}
            >
              (Reset Fairway engine key)
            </a>{" "}
            ·{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                resetWorkerUrl();
              }}
            >
              (Reset Proxy URL)
            </a>{" "}
            ·{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setWorkerAndToken();
              }}
            >
              (Set Worker URL & token)
            </a>{" "}
            ·{" "}
            <small>
              Draft input length: {inputCharCount.toLocaleString()} / 400,000 ·
              Route: {routeLabel}
            </small>
          </div>

          {result ? (
            <div style={{ marginTop: 8, fontSize: 14 }}>
              <strong>Fairway draft: </strong>
              {result.viewUrl ? (
                <a href={result.viewUrl} target="_blank" rel="noreferrer">
                  Open draft
                </a>
              ) : (
                "Draft created."
              )}
              {result.exportUrl ? (
                <>
                  {" · "}
                  <a href={result.exportUrl} target="_blank" rel="noreferrer">
                    Download export
                  </a>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}

/* ---------- small UI helpers ---------- */
function Small({ label, value, onChange, placeholder }) {
  return (
    <>
      <label style={styles.label}>{label}</label>
      <input
        style={styles.input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </>
  );
}

function Grid({ two, children }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: two ? "1fr 1fr" : "1fr",
        gap: 12,
      }}
    >
      {children}
    </div>
  );
}

/* ---------- styles ---------- */
const styles = {
  wrap: {
    maxWidth: 980,
    margin: "0 auto",
    padding: 20,
    fontFamily:
      "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Inter, Helvetica, Arial, sans-serif",
    color: "#111827",
    background: "#fafafa",
    minHeight: "100vh",
  },
  header: { display: "flex", alignItems: "center", gap: 12, marginBottom: 16 },
  badge: {
    width: 36,
    height: 36,
    borderRadius: 10,
    background: "#1f8f2c",
    color: "#fff",
    display: "grid",
    placeItems: "center",
    fontWeight: 700,
  },
  brand: { fontSize: 18, fontWeight: 700 },
  sub: { fontSize: 12, color: "#6b7280" },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 16,
  },
  cardTitle: { fontSize: 14, fontWeight: 700, marginBottom: 6 },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: "#6b7280",
    marginTop: 10,
    marginBottom: 6,
    display: "block",
  },
  input: {
    width: "100%",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 14,
    outline: "none",
    background: "#fff",
  },
  actions: { display: "flex", gap: 10, marginTop: 12 },
  primary: {
    background: "#1f8f2c",
    border: "1px solid #177020",
    color: "#fff",
    borderRadius: 10,
    padding: "10px 14px",
    cursor: "pointer",
    fontWeight: 600,
  },
  chipBtn: {
    border: "1px solid #e5e7eb",
    background: "#f8fafb",
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 12,
    cursor: "pointer",
    fontWeight: 600,
  },
};
